import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "@/lib/api";
import { Waveform, SealCheck, TrendUp, ArrowRight, Users, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { HollywoodCube } from "@/components/HollywoodCube";

const coverUrl = (a) => (a?.cover_path ? `${API}/media/${a.cover_path}` : null);

function scoreColor(s) {
  if (s >= 75) return "#E9AC0C";
  if (s >= 50) return "#13C4E8";
  if (s >= 30) return "#9D3FFF";
  return "#DC2450";
}

function Avatar({ src, name, size = 64 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (src) {
    return <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover border border-primary/40 shrink-0" />;
  }
  return (
    <div style={{ width: size, height: size }}
      className="rounded-full bg-secondary border border-border flex items-center justify-center font-head font-bold text-primary shrink-0"
    >{initial}</div>
  );
}

function ClientCard({ c, testid }) {
  const score = c.reputation_score;
  return (
    <Link to={`/p/${c.slug}`} data-testid={testid}
      className="block border border-border hover:border-primary transition-colors duration-200 p-5 group bg-card">
      <div className="flex items-center gap-4">
        <Avatar src={c.avatar} name={c.display_name} size={56} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="font-head font-bold text-lg truncate group-hover:text-primary transition-colors duration-200 normal-case">{c.display_name}</h3>
            <SealCheck size={16} weight="fill" className="text-primary shrink-0" />
          </div>
          {c.niche && <div className="text-xs text-muted-foreground truncate">{c.niche}</div>}
        </div>
        {score != null && (
          <div className="text-right shrink-0">
            <div className="font-mono text-xl font-bold" style={{ color: scoreColor(score) }}>{score}</div>
            <div className="overline text-muted-foreground text-[10px]">SCORE</div>
          </div>
        )}
      </div>
      {c.tagline && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.tagline}</p>}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        <span className="font-mono text-xs text-muted-foreground">{c.article_count} article{c.article_count === 1 ? "" : "s"} live</span>
        <span className="text-xs text-primary inline-flex items-center gap-1 opacity-80 group-hover:opacity-100">View profile <ArrowRight size={12} weight="bold" /></span>
      </div>
    </Link>
  );
}

// Compact rotating strip for the SMI Journal / other public pages
export function NetworkStrip() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/network").then(({ data }) => setData(data)).catch(() => setData({ clients: [], articles: [] })); }, []);
  if (!data || data.clients.length === 0) return null;
  const clients = data.clients.slice(0, 8);
  return (
    <div className="mb-12 border border-border bg-card p-6" data-testid="network-strip">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} weight="fill" className="text-primary" />
          <h2 className="font-head font-bold text-lg normal-case">Featured Members</h2>
        </div>
        <Link to="/network" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">See the whole network <ArrowRight size={12} weight="bold" /></Link>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
        {clients.map((c, i) => (
          <Link key={c.slug} to={`/p/${c.slug}`} data-testid={`strip-client-${i}`}
            className="shrink-0 w-40 border border-border hover:border-primary transition-colors duration-200 p-4 text-center bg-background group">
            <div className="flex justify-center mb-2"><Avatar src={c.avatar} name={c.display_name} size={56} /></div>
            <div className="font-head font-bold text-sm truncate group-hover:text-primary transition-colors duration-200 normal-case">{c.display_name}</div>
            {c.reputation_score != null && (
              <div className="font-mono text-xs mt-1" style={{ color: scoreColor(c.reputation_score) }}>Score {c.reputation_score}</div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Network() {
  const [data, setData] = useState(null);
  const [spot, setSpot] = useState(0);
  const timer = useRef(null);

  useEffect(() => { api.get("/public/network").then(({ data }) => setData(data)).catch(() => setData({ clients: [], articles: [], total_clients: 0 })); }, []);

  const clients = data?.clients || [];
  const articles = data?.articles || [];

  // auto-rotating spotlight
  useEffect(() => {
    if (clients.length < 2) return;
    timer.current = setInterval(() => setSpot((s) => (s + 1) % clients.length), 4500);
    return () => clearInterval(timer.current);
  }, [clients.length]);

  const go = (dir) => {
    clearInterval(timer.current);
    setSpot((s) => (s + dir + clients.length) % clients.length);
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>The SMI Network — Featured Clients & Creators</title>
        <meta name="description" content="Explore the Social Media Influencer network: verified creators, artists, entrepreneurs and influencers building their reputation across the web." />
        <link rel="canonical" href={`${window.location.origin}/network`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="The SMI Network — Featured Clients" />
        <meta property="og:description" content="Verified members of the Social Media Influencer network." />
        <meta property="og:url" content={`${window.location.origin}/network`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "The SMI Network",
            description: "Featured clients and creators on Social Media Influencer.",
            url: `${window.location.origin}/network`,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: clients.slice(0, 25).map((c, i) => ({
                "@type": "ListItem", position: i + 1, name: c.display_name,
                url: `${window.location.origin}/p/${c.slug}`,
              })),
            },
          })}
        </script>
      </Helmet>

      <header className="border-b border-border sticky top-0 glass z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/network" className="flex items-center gap-2">
            <Waveform size={24} weight="fill" className="text-primary" />
            <span className="font-head font-bold text-xl tracking-tighter gold-foil">SMI</span>
            <span className="overline text-muted-foreground ml-1">NETWORK</span>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">Journal</Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-10 flex items-start justify-between gap-6">
          <div>
            <div className="overline text-primary mb-2">SMI · THE NETWORK · 24/7</div>
            <h1 className="font-head font-bold text-5xl sm:text-6xl tracking-tighter"><span className="gold-foil">Featured Clients</span></h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Every SMI member, published and promoted across our domain. Verified creators, artists, entrepreneurs and influencers — {data?.total_clients ?? "…"} and growing.</p>
          </div>
          <div className="hidden md:block pt-2"><HollywoodCube size={100} /></div>
        </div>

        {data === null && <div className="text-muted-foreground font-mono text-sm">Loading the network…</div>}
        {data && clients.length === 0 && (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="network-empty">
            No client profiles are live yet. Once members publish reputation content, they appear here automatically.
          </div>
        )}

        {/* Auto-rotating spotlight */}
        {clients.length > 0 && (
          <div className="relative border border-primary/30 bg-card overflow-hidden mb-12" data-testid="network-spotlight">
            {(() => {
              const c = clients[spot];
              return (
                <div className="p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-8">
                  <Avatar src={c.avatar} name={c.display_name} size={128} />
                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <div className="overline text-primary mb-2 flex items-center gap-2 justify-center sm:justify-start"><TrendUp size={12} weight="fill" /> IN THE SPOTLIGHT</div>
                    <div className="flex items-center gap-2 justify-center sm:justify-start">
                      <h2 className="font-head font-bold text-3xl tracking-tight normal-case truncate">{c.display_name}</h2>
                      <SealCheck size={22} weight="fill" className="text-primary shrink-0" />
                    </div>
                    {c.niche && <div className="text-sm text-muted-foreground mt-1">{c.niche}</div>}
                    {c.tagline && <p className="text-sm text-muted-foreground mt-3 max-w-xl">{c.tagline}</p>}
                    <div className="flex items-center gap-6 mt-5 justify-center sm:justify-start">
                      {c.reputation_score != null && (
                        <div>
                          <div className="font-mono text-3xl font-bold" style={{ color: scoreColor(c.reputation_score) }}>{c.reputation_score}</div>
                          <div className="overline text-muted-foreground">REPUTATION</div>
                        </div>
                      )}
                      <div>
                        <div className="font-mono text-3xl font-bold">{c.article_count}</div>
                        <div className="overline text-muted-foreground">ARTICLES LIVE</div>
                      </div>
                      <Link to={`/p/${c.slug}`} data-testid="spotlight-view-btn"
                        className="bg-primary text-primary-foreground font-semibold px-5 py-3 text-sm inline-flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200">
                        View profile <ArrowRight size={16} weight="bold" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })()}
            {clients.length > 1 && (
              <>
                <button onClick={() => go(-1)} data-testid="spotlight-prev" className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center bg-background/70 border border-border hover:border-primary transition-colors duration-200"><CaretLeft size={18} weight="bold" /></button>
                <button onClick={() => go(1)} data-testid="spotlight-next" className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center bg-background/70 border border-border hover:border-primary transition-colors duration-200"><CaretRight size={18} weight="bold" /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {clients.slice(0, 12).map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === spot ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* All clients grid */}
        {clients.length > 0 && (
          <>
            <h2 className="font-head font-bold text-2xl mb-5 normal-case">All members</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14" data-testid="network-grid">
              {clients.map((c, i) => <ClientCard key={c.slug} c={c} testid={`network-client-${i}`} />)}
            </div>
          </>
        )}

        {/* Rotating client articles */}
        {articles.length > 0 && (
          <>
            <h2 className="font-head font-bold text-2xl mb-5 normal-case">Latest from our members</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="network-articles">
              {articles.slice(0, 12).map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`network-article-${a.slug}`}
                  className="block relative border border-border hover:border-primary transition-colors duration-200 group overflow-hidden bg-card">
                  <div className="absolute top-3 right-3 z-10 opacity-80 group-hover:opacity-100 transition-opacity duration-200"><HollywoodCube size={30} /></div>
                  {coverUrl(a) && (
                    <div className="aspect-[16/9] overflow-hidden border-b border-border">
                      <img src={coverUrl(a)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="overline text-muted-foreground mb-2">{a.author}</div>
                    <h3 className="font-head font-bold text-base group-hover:text-primary transition-colors duration-200 normal-case line-clamp-2">{a.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.meta_description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
